import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { QRCodeSVG } from "qrcode.react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, IndianRupee } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const periodLabel = (p) => { const [y, m] = p.split("-"); return `${MONTHS[parseInt(m, 10) - 1]} ${y}`; };

export const BillingReminder = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get("/billing/summary").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data || !data.has_due) return null;
  const months = data.unpaid || [];
  const total = months.length * data.amount;

  return (
    <>
      <Card className="p-4 border-amber-300 bg-amber-50/70 flex items-center justify-between gap-4 flex-wrap" data-testid="billing-reminder-banner">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-amber-900">Anila subscription payment due</div>
            <div className="text-sm text-amber-800/90">
              {months.length === 1
                ? <>Payment for <b>{periodLabel(months[0].period)}</b> is due — {inr(data.amount)}.</>
                : <><b>{months.length} months</b> pending ({periodLabel(months[0].period)} – {periodLabel(months[months.length - 1].period)}) — total {inr(total)}.</>}
              {" "}Please pay to AUM Online.
            </div>
          </div>
        </div>
        <Button className="gap-2" onClick={() => setOpen(true)} data-testid="billing-pay-open"><IndianRupee className="h-4 w-4" /> Pay now</Button>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Pay AUM Online — {inr(data.amount)}/month</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-xl bg-white p-4 border" data-testid="billing-qr">
              <QRCodeSVG value={data.upi_link} size={200} level="M" includeMargin />
            </div>
            <div className="text-center text-sm">
              <div className="font-semibold">{data.payee}</div>
              <div className="text-xs text-muted-foreground font-mono">{data.vpa}</div>
              <div className="mt-1">Scan with any UPI app, or tap below on your phone.</div>
            </div>
            <a href={data.upi_link} className="w-full" data-testid="billing-pay-upi">
              <Button className="w-full gap-2"><IndianRupee className="h-4 w-4" /> Pay {inr(data.amount)}{months.length > 1 ? " (per month)" : ""}</Button>
            </a>
            <div className="text-xs text-muted-foreground text-center border-t pt-3">
              After paying, please <b>send the payment screenshot to AUM Online on WhatsApp</b>. Your month will be marked paid once confirmed.
            </div>
            {months.length > 1 && (
              <div className="w-full text-xs text-muted-foreground">
                <div className="font-medium mb-1">Pending months:</div>
                <div className="flex flex-wrap gap-1">
                  {months.map((m) => <Badge key={m.period} variant="outline">{periodLabel(m.period)}</Badge>)}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
